/**
 * ACTIVITY-CORE — human feed texts (i18n).
 *
 * APP-SPECIFIC part (adapt per clone): the entity_type+action →
 * phrase map. The structure (patch, grouping, offline) lives in @agape/shared.
 * Descriptors `msg` (not strings) are returned so the language stays reactive.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@agape/shared";

interface ActivitySentence {
  /** Phrase with `{name}` interpolated. */
  named: MessageDescriptor;
  /** Phrase without a name (the patch does not contain one). */
  anonymous: MessageDescriptor;
}

/**
 * Fine-grained semantic key: refines `updated`/`deleted` with what really
 * happened (complete, move to trash…) by looking at the patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (action === "deleted") {
    // deleted_at transition = trash; full snapshot = permanent purge.
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

// Agape domain (F0b). Unlisted actions/entities fall back to FALLBACK.
// The visible title comes from the patch (name/title) via `activityEntityName`.
const ENTITY_TEXTS: Record<string, Record<string, ActivitySentence>> = {
  church: {
    created: { named: msg`added the church "{name}"`, anonymous: msg`added a church` },
    updated: { named: msg`updated the church "{name}"`, anonymous: msg`updated a church` },
    trashed: {
      named: msg`moved the church "{name}" to the trash`,
      anonymous: msg`moved a church to the trash`,
    },
    restored: {
      named: msg`restored the church "{name}" from the trash`,
      anonymous: msg`restored a church from the trash`,
    },
  },
  venue: {
    created: { named: msg`added the venue "{name}"`, anonymous: msg`added a venue` },
    updated: { named: msg`updated the venue "{name}"`, anonymous: msg`updated a venue` },
    trashed: {
      named: msg`moved the venue "{name}" to the trash`,
      anonymous: msg`moved a venue to the trash`,
    },
    restored: {
      named: msg`restored the venue "{name}" from the trash`,
      anonymous: msg`restored a venue from the trash`,
    },
  },
  person: {
    created: { named: msg`added "{name}" to the directory`, anonymous: msg`added a person` },
    updated: { named: msg`updated the profile of "{name}"`, anonymous: msg`updated a profile` },
    trashed: {
      named: msg`moved "{name}" to the trash`,
      anonymous: msg`moved a person to the trash`,
    },
    restored: {
      named: msg`restored "{name}" from the trash`,
      anonymous: msg`restored a person from the trash`,
    },
  },
  person_relation: {
    created: { named: msg`linked family members`, anonymous: msg`linked family members` },
    deleted: { named: msg`unlinked family members`, anonymous: msg`unlinked family members` },
  },
  membership: {
    created: { named: msg`registered a membership`, anonymous: msg`registered a membership` },
    updated: { named: msg`updated a membership`, anonymous: msg`updated a membership` },
  },
  group_type: {
    created: { named: msg`created the group type "{name}"`, anonymous: msg`created a group type` },
    updated: { named: msg`updated the group type "{name}"`, anonymous: msg`updated a group type` },
  },
  church_group: {
    created: { named: msg`created the group "{name}"`, anonymous: msg`created a group` },
    updated: { named: msg`updated the group "{name}"`, anonymous: msg`updated a group` },
    trashed: {
      named: msg`moved the group "{name}" to the trash`,
      anonymous: msg`moved a group to the trash`,
    },
    restored: {
      named: msg`restored the group "{name}" from the trash`,
      anonymous: msg`restored a group from the trash`,
    },
  },
  group_member: {
    created: { named: msg`added someone to a group`, anonymous: msg`added someone to a group` },
    updated: {
      named: msg`changed a member's function`,
      anonymous: msg`changed a member's function`,
    },
    deleted: {
      named: msg`removed someone from a group`,
      anonymous: msg`removed someone from a group`,
    },
  },
  projection_item: {
    created: { named: msg`added "{name}" to projection`, anonymous: msg`added a projection item` },
    updated: {
      named: msg`updated the projection item "{name}"`,
      anonymous: msg`updated a projection item`,
    },
    trashed: {
      named: msg`moved the projection item "{name}" to the trash`,
      anonymous: msg`moved a projection item to the trash`,
    },
    restored: {
      named: msg`restored the projection item "{name}" from the trash`,
      anonymous: msg`restored a projection item from the trash`,
    },
  },
  finance_record: {
    created: { named: msg`recorded a finance entry`, anonymous: msg`recorded a finance entry` },
    updated: { named: msg`updated a finance entry`, anonymous: msg`updated a finance entry` },
  },
  app_role: {
    created: { named: msg`created the role "{name}"`, anonymous: msg`created a role` },
    updated: { named: msg`updated the role "{name}"`, anonymous: msg`updated a role` },
  },
  account_role: {
    created: { named: msg`assigned a role`, anonymous: msg`assigned a role` },
    deleted: { named: msg`revoked a role`, anonymous: msg`revoked a role` },
  },
  permission_grant: {
    created: { named: msg`granted a permission`, anonymous: msg`granted a permission` },
    updated: { named: msg`updated a permission`, anonymous: msg`updated a permission` },
    deleted: { named: msg`revoked a permission`, anonymous: msg`revoked a permission` },
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Visible name of the entity extracted from the patch (text/name, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["text", "name", "title"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * i18n phrase of an entry: descriptor + values. Render with
 * `t(descriptor, values)` (or i18n._) in the component.
 */
export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table = ENTITY_TEXTS[row.entity_type ?? ""] ?? {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Localized "5 min ago" with Intl; falls back to day/date for old times. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
